import { useState } from 'react';
import { type SortingState } from '@tanstack/react-table';
import { PermissionGuard } from '@/features/auth';
import { useDealersQuery } from '../hooks/useDealersQuery';
import { useDealerStatusesQuery } from '../hooks/useDealerStatusesQuery';
import { SearchBar, Pagination, Select, ButtonLink } from '@/shared/components';
import { Checkbox } from '@/shared/components/form';
import { DealerDataTable } from '../components/DealerDataTable';
import { DEALER_TYPE_OPTIONS, type DealerType } from '../types/dealers.types';

interface PaginationConfig {
  page: number;
  per_page: number;
}

export function DealerList() {
  const [search, setSearch] = useState('');
  const [groupsOnly, setGroupsOnly] = useState(false);
  const [typeFilter, setTypeFilter] = useState<DealerType | ''>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationConfig>({ page: 1, per_page: 20 });
  const [sorting, setSorting] = useState<SortingState>([]);

  const resetPage = () => setPagination((prev) => ({ ...prev, page: 1 }));

  const handleSortingChange = (s: SortingState) => {
    setSorting(s);
    resetPage();
  };

  const sortBy  = sorting[0]?.id ?? '';
  const sortDir = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : 'asc';

  const { data: statusCatalog = [], isLoading: statusesLoading } = useDealerStatusesQuery();

  const typeOptions = [
    { value: '', label: 'All types' },
    ...DEALER_TYPE_OPTIONS,
  ];
  const statusOptions = [
    { value: '', label: 'All statuses' },
    ...statusCatalog.map((s) => ({ value: s.key, label: s.label })),
  ];

  const query = useDealersQuery({
    search: search.length >= 2 ? search : undefined,
    page: pagination.page,
    per_page: pagination.per_page,
    is_group: groupsOnly ? true : undefined,
    type: typeFilter || undefined,
    status: statusFilter || undefined,
    sort_by: sortBy || undefined,
    sort_dir: sortBy ? sortDir : undefined,
  });

  const dealers = query.data?.data ?? [];
  const meta = query.data?.meta;

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Dealers</h1>
        <PermissionGuard permission={{ resource: 'dealer', action: 'add' }}>
          <ButtonLink to="/admin/dealers/new" size="sm">New Dealer</ButtonLink>
        </PermissionGuard>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <SearchBar
          searchTerm={search}
          setSearchTerm={(term) => { setSearch(term); resetPage(); }}
          placeholder="Search dealers"
        />
        <Select
          size="sm"
          options={typeOptions}
          value={typeFilter}
          placeholder="All types"
          onChange={(v) => { setTypeFilter(v as DealerType | ''); resetPage(); }}
        />
        <Select
          size="sm"
          options={statusOptions}
          value={statusFilter}
          placeholder="All statuses"
          isLoading={statusesLoading}
          onChange={(v) => { setStatusFilter(v); resetPage(); }}
        />
        <Checkbox
          label="Groups only"
          checked={groupsOnly}
          onChange={(checked) => { setGroupsOnly(checked); resetPage(); }}
        />
      </div>

      <div className="space-y-4">
        <DealerDataTable
            data={dealers}
            isLoading={query.isLoading}
            sorting={sorting}
            onSortingChange={handleSortingChange}
          />

        {meta && meta.last_page > 1 && (
          <Pagination
            currentPage={meta.current_page}
            lastPage={meta.last_page}
            total={meta.total}
            perPage={meta.per_page}
            from={meta.from}
            to={meta.to}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
            onPerPageChange={(per_page) => setPagination({ page: 1, per_page })}
          />
        )}
      </div>
    </section>
  );
}
